/*
 * Scroll-scrubbed video.
 *
 * The section is taller than the viewport and its inner wrapper is sticky, so
 * while the section passes the viewport the film stays pinned and its playhead
 * is driven by scroll position instead of by time.
 *
 * Three things make this work, and all three are easy to get wrong:
 *
 * 1. The mp4 must be encoded with EVERY frame a keyframe. Seeking otherwise
 *    snaps to the nearest keyframe (often seconds apart) and the scrub judders.
 *    cr8-hero-scrub.mp4 is encoded that way; a normal export is not.
 * 2. currentTime is eased toward the scroll target inside rAF rather than set
 *    directly on the scroll event. Setting it directly tracks the wheel exactly
 *    but looks mechanical, and fires far more seeks than the decoder can serve.
 * 3. The rAF loop is gated on a synchronous rect check, NOT an
 *    IntersectionObserver. An observer's callback is async: it lands after the
 *    first frame has already run, and on a hero that is on screen at load it
 *    has no change to report, so a loop gated on it stops on frame one and
 *    never restarts. The scroll listener below is the belt to that braces —
 *    any scroll revives a stopped loop.
 */
(function () {
  'use strict';

  // The script tag lives in the section, so it would run again if the section
  // were ever placed twice on one page. One boot only.
  if (window.__cr8ScrollScrubLoaded) return;
  window.__cr8ScrollScrubLoaded = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(section) {
    var video = section.querySelector('[data-cr8-scrub-video]');
    if (!video || section.__cr8ScrubInit) return;
    section.__cr8ScrubInit = true;

    // Reduced motion: no scrubbing at all. Park the playhead on a
    // representative frame and leave it as a still.
    if (reduced.matches) {
      var park = function () {
        try { video.currentTime = video.duration * 0.18; } catch (e) {}
      };
      if (video.readyState >= 1) park();
      else video.addEventListener('loadedmetadata', park, { once: true });
      return;
    }

    var current = 0;
    var duration = 0;
    var raf = null;

    function readDuration() {
      // Some browsers report Infinity until the file is fully buffered.
      if (isFinite(video.duration) && video.duration > 0) duration = video.duration;
    }

    function frame() {
      var rect = section.getBoundingClientRect();

      // Off screen: stop burning frames. Any scroll or resize restarts us.
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
        raf = null;
        return;
      }

      if (!duration) readDuration();

      if (duration) {
        var travel = rect.height - window.innerHeight;
        var p = travel > 0 ? -rect.top / travel : 0;
        if (p < 0) p = 0;
        else if (p > 1) p = 1;

        var target = p * duration;
        // Ease toward the target; snap when close so it settles instead of
        // creeping for ever.
        var delta = target - current;
        current = Math.abs(delta) < 0.005 ? target : current + delta * 0.14;

        if (video.readyState >= 1) {
          try { video.currentTime = current; } catch (e) {}
        }
      }

      raf = window.requestAnimationFrame(frame);
    }

    function start() {
      if (raf === null && !document.hidden) raf = window.requestAnimationFrame(frame);
    }

    // iOS will not honour programmatic seeks until the element has been handed
    // to the decoder once. A muted inline play/pause pair does that silently.
    function prime() {
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () { video.pause(); }).catch(function () {});
      } else {
        try { video.pause(); } catch (e) {}
      }
    }

    video.addEventListener('loadedmetadata', function () {
      readDuration();
      prime();
      start();
    }, { once: true });

    // A cached video has already fired loadedmetadata by now.
    if (video.readyState >= 1) { readDuration(); prime(); start(); }

    window.addEventListener('scroll', start, { passive: true });
    window.addEventListener('resize', start, { passive: true });
    document.addEventListener('visibilitychange', start);

    // A one-off nudge on first touch, for the iOS cases where the priming play
    // was rejected before any user gesture existed.
    document.addEventListener('touchstart', prime, { once: true, passive: true });

    start();
  }

  function boot() {
    document.querySelectorAll('[data-cr8-scrub]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-cr8-scrub]');
    if (s) init(s);
  });
})();
